// src/game-system/races/index.js

// Import all race classes
import { Human } from './human';
import { Elf } from './elf';
import { Dwarf } from './dwarf';
import { Dragonborn } from './dragonborn';
import { Halfling } from './halfling';
import { Gnome } from './gnome';
import { BaseRace } from './baseRace';

/**
 * @typedef {Object} RaceDefinition
 * @property {string} id - Unique identifier for the race
 * @property {string} name - Display name of the race
 * @property {string} description - Brief description
 * @property {string[]} availableSubraces - List of available subraces
 * @property {Function} constructor - The race's constructor class
 */

/**
 * Available races with their configurations
 * @type {Object.<string, RaceDefinition>}
 */
export const AVAILABLE_RACES = {
  human: {
    id: 'human',
    name: 'Human',
    description: 'Versatile and ambitious, humans are adaptable to any situation',
    availableSubraces: ['standard', 'variant'],
    constructor: Human
  },
  elf: {
    id: 'elf',
    name: 'Elf',
    description: 'Graceful and long-lived, masters of magic and warfare',
    availableSubraces: ['high', 'wood'],
    constructor: Elf
  },
  dwarf: {
    id: 'dwarf',
    name: 'Dwarf',
    description: 'Hardy and traditional, masters of stone and metal',
    availableSubraces: ['hill', 'mountain'],
    constructor: Dwarf
  },
  dragonborn: {
    id: 'dragonborn',
    name: 'Dragonborn',
    description: 'Proud draconic warriors with powerful breath weapons',
    availableSubraces: [
      'black', 'blue', 'brass', 'bronze', 'copper',
      'gold', 'green', 'red', 'silver', 'white'
    ],
    constructor: Dragonborn
  },
  halfling: {
    id: 'halfling',
    name: 'Halfling',
    description: 'Quick and lucky, natural storytellers and adventurers',
    availableSubraces: ['lightfoot', 'stout'],
    constructor: Halfling
  },
  gnome: {
    id: 'gnome',
    name: 'Gnome',
    description: 'Innovative and magical, masters of illusion and invention',
    availableSubraces: ['forest', 'rock'],
    constructor: Gnome
  }
};

/**
 * Size categories for races
 * @type {Object.<string, string>}
 */
export const SIZE_CATEGORIES = {
  SMALL: 'Small',
  MEDIUM: 'Medium',
  LARGE: 'Large'
};

/**
 * Create a new character race instance
 * @param {string} raceId - ID of the race to create
 * @param {string} [subraceId] - ID of the subrace to use
 * @returns {BaseRace} Instance of the specified race
 * @throws {Error} If race or subrace is invalid
 */
export function createRace(raceId, subraceId) {
  const raceDefinition = AVAILABLE_RACES[raceId];
  if (!raceDefinition) {
    throw new Error(`Invalid race: ${raceId}`);
  }

  if (subraceId && !raceDefinition.availableSubraces.includes(subraceId)) {
    throw new Error(`Invalid subrace ${subraceId} for race ${raceId}`);
  }

  // Check if the constructor is a class (like Elf, Dwarf) or an instance (like Human)
  if (typeof raceDefinition.constructor === 'function' &&
      Object.getPrototypeOf(raceDefinition.constructor).toString().includes('class')) {
    // It's a class constructor, instantiate it
    return new raceDefinition.constructor(subraceId);
  } else {
    // It's already an instance, just return it
    return raceDefinition.constructor;
  }
}

/**
 * Get available subraces for a given race
 * @param {string} raceId - ID of the race
 * @returns {string[]} Array of available subrace IDs
 * @throws {Error} If race is invalid
 */
export function getAvailableSubraces(raceId) {
  const raceDefinition = AVAILABLE_RACES[raceId];
  if (!raceDefinition) {
    throw new Error(`Invalid race: ${raceId}`);
  }
  return raceDefinition.availableSubraces;
}

/**
 * Get all races that have a specific attribute bonus
 * @param {string} attribute - Attribute to check for
 * @returns {string[]} Array of race IDs that grant a bonus to the attribute
 */
export function getRacesByAttributeBonus(attribute) {
  return Object.entries(AVAILABLE_RACES)
    .filter(([_, race]) => {
      let instance;
      if (typeof race.constructor === 'function' &&
          Object.getPrototypeOf(race.constructor).toString().includes('class')) {
        instance = new race.constructor();
      } else {
        instance = race.constructor;
      }
      return instance.attributeBonuses[attribute] > 0;
    })
    .map(([id, _]) => id);
}

/**
 * Get races by size category
 * @param {string} sizeCategory - Size category to filter by
 * @returns {string[]} Array of race IDs of the specified size
 */
export function getRacesBySize(sizeCategory) {
  return Object.entries(AVAILABLE_RACES)
    .filter(([_, race]) => {
      let instance;
      if (typeof race.constructor === 'function' &&
          Object.getPrototypeOf(race.constructor).toString().includes('class')) {
        instance = new race.constructor();
      } else {
        instance = race.constructor;
      }
      return instance.size.category === sizeCategory;
    })
    .map(([id, _]) => id);
}

/**
 * Validate if a race/subrace combination is valid
 * @param {string} raceId - ID of the race
 * @param {string} [subraceId] - ID of the subrace
 * @returns {boolean} True if valid combination
 */
export function isValidRaceCombination(raceId, subraceId) {
  try {
    const race = AVAILABLE_RACES[raceId];
    if (!race) return false;
    if (subraceId && !race.availableSubraces.includes(subraceId)) return false;
    return true;
  } catch {
    return false;
  }
}

// Export all race classes
export {
  Human,
  Elf,
  Dwarf,
  Dragonborn,
  Halfling,
  Gnome,
  BaseRace
};

// Export helper functions for specific race features
export * from './helpers/raceFeatures';

// Export type definitions
export * from './types';

// Example usage:
/*
// Create a new race instance
const highElf = createRace('elf', 'high');

// Get available subraces for a race
const dwarfSubraces = getAvailableSubraces('dwarf');

// Find races with Strength bonus
const strongRaces = getRacesByAttributeBonus('strength');

// Find all small races
const smallRaces = getRacesBySize(SIZE_CATEGORIES.SMALL);

// Validate a race combination
const isValid = isValidRaceCombination('elf', 'high');
*/